import { Box, MenuItem, Select, Switch, Typography } from "@material-ui/core";
import { useCallback, useState } from "react";
import { EPOCUS_TEXT_SECONDARY, rem } from "../../_lib/epocus-palette";
import {
  SettingsCard,
  SettingsCardLabel,
  SettingsCardLabelRow,
} from "./settings-card";

const STORAGE_PREFIX = "epocus.reviewer.";

const readStored = (key: string, fallback: string): string => {
  try {
    return localStorage.getItem(`${STORAGE_PREFIX}${key}`) ?? fallback;
  } catch {
    return fallback;
  }
};

const writeStored = (key: string, value: string): void => {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${key}`, value);
  } catch {
    // A blocked or full store only costs the preference, never the page.
  }
};

const useStoredPreference = (
  key: string,
  fallback: string,
): [string, (next: string) => void] => {
  const [value, setValue] = useState(() => readStored(key, fallback));

  const update = useCallback(
    (next: string) => {
      setValue(next);
      writeStored(key, next);
    },
    [key],
  );

  return [value, update];
};

const PreferenceRow: FCwC<{ label: string }> = ({ label, children }) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    style={{ gap: 12, minHeight: 40 }}
  >
    <Typography style={{ fontSize: rem(14) }}>{label}</Typography>
    {children}
  </Box>
);

const ToggleRow: FC<{ label: string; storageKey: string }> = ({
  label,
  storageKey,
}) => {
  const [value, setValue] = useStoredPreference(storageKey, "on");

  return (
    <PreferenceRow label={label}>
      <Switch
        color="primary"
        checked={value === "on"}
        onChange={(event) => setValue(event.target.checked ? "on" : "off")}
        inputProps={{ "aria-label": label }}
      />
    </PreferenceRow>
  );
};

export const ReviewerNotificationsCard: FC = () => (
  <SettingsCard>
    <SettingsCardLabelRow>
      <SettingsCardLabel>Notifications</SettingsCardLabel>
    </SettingsCardLabelRow>
    <ToggleRow
      label="New assignment to my queue"
      storageKey="notify.assigned"
    />
    <ToggleRow
      label="Approaching turnaround target"
      storageKey="notify.turnaround"
    />
    <Typography
      style={{ fontSize: rem(12), color: EPOCUS_TEXT_SECONDARY, marginTop: 4 }}
    >
      Stored on this device.
    </Typography>
  </SettingsCard>
);

export const ReviewerPreferencesCard: FC = () => {
  const [queueSort, setQueueSort] = useStoredPreference("queueSort", "oldest");
  const [language, setLanguage] = useStoredPreference("language", "en");

  return (
    <SettingsCard>
      <SettingsCardLabelRow>
        <SettingsCardLabel>Preferences</SettingsCardLabel>
      </SettingsCardLabelRow>
      <PreferenceRow label="Default queue sort">
        <Select
          value={queueSort}
          onChange={(event) => setQueueSort(String(event.target.value))}
          inputProps={{ "aria-label": "Default queue sort" }}
        >
          <MenuItem value="oldest">Oldest first</MenuItem>
          <MenuItem value="newest">Newest first</MenuItem>
        </Select>
      </PreferenceRow>
      <PreferenceRow label="Language">
        <Select
          value={language}
          onChange={(event) => setLanguage(String(event.target.value))}
          inputProps={{ "aria-label": "Language" }}
        >
          <MenuItem value="en">English</MenuItem>
        </Select>
      </PreferenceRow>
    </SettingsCard>
  );
};
